import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export const inputCls =
  "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500";

export function Field({ label, required, error, hint, children, className, errorTestId, hintTestId }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {hint && <p data-testid={hintTestId} className="text-xs text-slate-500">{hint}</p>}
      {error && <p data-testid={errorTestId} className="text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
}

export const TextInput = forwardRef(({ className, ...props }, ref) => (
  <input ref={ref} {...props} className={cn(inputCls, className)} />
));
TextInput.displayName = "TextInput";

export function SelectInput({ options, placeholder = "-- Select --", className, ...props }) {
  return (
    <select {...props} className={cn(inputCls, "pr-8", className)}>
      {placeholder !== null && <option value="">{placeholder}</option>}
      {options.map((o) =>
        typeof o === "string" ? (
          <option key={o} value={o}>{o}</option>
        ) : (
          <option key={o.value} value={o.value}>{o.label}</option>
        )
      )}
    </select>
  );
}

export function PhoneInput({ value, onChange, testid, ...props }) {
  return (
    <div className="flex">
      <span data-testid={`${testid}-country-code`} className="inline-flex items-center gap-1.5 px-3 rounded-l-md border border-r-0 border-slate-300 bg-slate-50 text-sm text-slate-700 whitespace-nowrap">
        India (+91)
      </span>
      <input
        data-testid={testid}
        inputMode="numeric"
        maxLength={10}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
        className={cn(inputCls, "rounded-l-none")}
        placeholder="10 digit mobile number"
        {...props}
      />
    </div>
  );
}

export function Section({ title, description, actions, children, testid }) {
  return (
    <section data-testid={testid} className="bg-white rounded-lg border border-slate-200 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 px-6 py-4 border-b border-slate-100">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
        </div>
        {actions}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

export function StatusBadge({ status, testid }) {
  const ok = status === "WORKING";
  return (
    <span data-testid={testid} className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide",
      ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"
    )}>
      <span className={cn("w-1.5 h-1.5 rounded-full", ok ? "bg-emerald-500" : "bg-slate-400")} />
      {status || "-"}
    </span>
  );
}
